import React from 'react'

import { css, sva } from '@portal/styled-system/css'

import { env, isFeatureEnabled } from '@/shared/config'
import { getAssetUrl } from '@/shared/lib/cdn'
import { useI18n } from '@/shared/lib/i18n'

type PasswordRequirementListProps = {
    passwordLongEnough: boolean
    passwordHasLowerAndUppercase: boolean
    passwordHasDigitOrSpecialCharacter: boolean
}

export function PasswordRequirementList(props: PasswordRequirementListProps) {
    const { passwordLongEnough, passwordHasLowerAndUppercase, passwordHasDigitOrSpecialCharacter } = props

    return (
        <div className={css({ display: 'flex', flexDirection: 'column', gap: '0.5', pt: '2' })}>
            <PasswordLongEnough isChecked={passwordLongEnough} />

            {isFeatureEnabled('strong-password-validation') && (
                <React.Fragment>
                    <PasswordHasLowerAndUppercase isChecked={passwordHasLowerAndUppercase} />
                    <PasswordHasDigitOrSpecialCharacter isChecked={passwordHasDigitOrSpecialCharacter} />
                </React.Fragment>
            )}
        </div>
    )
}

function PasswordLongEnough({ isChecked }: { isChecked: boolean }) {
    const { i18n } = useI18n()

    return (
        <CheckListElement
            isChecked={isChecked}
            title={i18n.PASSWORD_TOO_SHORT.replace('{number01}', env.ACCOUNT_PASSWORD_MIN_LEN.toString())}
        />
    )
}

function PasswordHasLowerAndUppercase({ isChecked }: { isChecked: boolean }) {
    const { i18n } = useI18n()

    return <CheckListElement isChecked={isChecked} title={i18n.PASSWORD_ALL_CASES} />
}

function PasswordHasDigitOrSpecialCharacter({ isChecked }: { isChecked: boolean }) {
    const { i18n } = useI18n()

    return <CheckListElement isChecked={isChecked} title={i18n.PASSWORD_SPECIAL_SYMBOLS} />
}

type CheckListElementProps = {
    isChecked: boolean
    title: string
}

function CheckListElement({ isChecked, title }: CheckListElementProps) {
    const classes = iconRecipe({ isChecked })
    return (
        <div className={classes.root}>
            <div className={classes.iconWrapper}>
                {isChecked ? (
                    <img
                        className={css({
                            filter: '[brightness(0) saturate(100%) invert(70%) sepia(20%) saturate(765%) hue-rotate(143deg) brightness(96%) contrast(98%)]',
                        })}
                        src={getAssetUrl('/assets/check.svg')}
                        height="12"
                        width="12"
                        alt=""
                    />
                ) : (
                    <div className={classes.icon} />
                )}
            </div>
            <span className={classes.label}>{title}</span>
        </div>
    )
}

const iconRecipe = sva({
    slots: ['root', 'label', 'iconWrapper', 'icon'],
    base: {
        root: {
            display: 'flex',
            gap: '1',
            textAlign: 'left',
        },
        label: { color: 'gray-v2.700' },
        iconWrapper: {
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: '3',
            minWidth: '3',
            height: '3',
            borderRadius: 'round',
            marginTop: '1',
        },
        icon: {
            width: '3',
            height: '3',
            borderRadius: 'round',
            border: 'default',
            borderColor: 'gray-v2',
        },
    },
    variants: {
        isChecked: {
            true: { iconWrapper: { backgroundColor: 'cyan-v2.100', border: 'default', borderColor: 'cyan-v2.600' } },
            false: {},
        },
    },
    defaultVariants: {
        isChecked: false,
    },
})
